"use client";

import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { motion } from "framer-motion";
import { EyeIcon, EyeOffIcon, Mail, Lock, User, ChevronRight } from "lucide-react";
import Link from "next/link";
import { toast } from "react-toastify";

interface SignUpFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  userType: any;
  handleUserTypeChange: (rolId: string) => void;
  handleNextStep: (e: React.MouseEvent<HTMLButtonElement>) => void;
  rol: any[];
  isSelectOpen: boolean;
  setIsSelectOpen: (isOpen: boolean) => void;
}

// Variantes para animaciones
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

export default function SignUpForm({
  email,
  setEmail,
  password,
  setPassword,
  userType,
  handleUserTypeChange,
  handleNextStep,
  rol,
  isSelectOpen,
  setIsSelectOpen
}: SignUpFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isFocusedEmail, setIsFocusedEmail] = useState(false);
  const [isFocusedPassword, setIsFocusedPassword] = useState(false);

  // Efecto para limpiar el estilo de overflow cuando el componente se desmonte
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    return passwordRegex.test(password);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setIsEmailValid(validateEmail(e.target.value));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setIsPasswordValid(validatePassword(e.target.value));
  };

  return (
    <div className="w-full space-y-5 3xl:space-y-8 bg-white/20 backdrop-blur-xl p-2 pb-5 px-8 3xl:p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)] transition-all duration-300 border border-white/20 dark:border-gray-700/30"
      style={{
        backgroundImage: "radial-gradient(circle at 100% 0%, rgba(44, 153, 255, 0.08) 0%, transparent 25%)",
        backgroundSize: "cover"
      }}
    >
      {/* Logo y título */}
      <div className="flex justify-center relative mb-6">
        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-24 h-24 bg-blue-400/30 dark:bg-blue-600/20 rounded-full blur-xl"></div>
      </div>

      <span className="w-full flex justify-center items-center flex-col gap-3 xl:gap-4 text-2xl xl:text-3xl font-black tracking-tight bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent text-center">
        ÚNETE A SURFWAY

        <div className="w-20 h-1.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full mx-auto"></div> 
      </span>

      <motion.p
        variants={itemVariants}
        className="text-sm md:text-md text-gray-700 leading-relaxed text-center"
      >
        Completa tus datos para registrarte y comenzar a disfrutar de todos nuestros servicios.
      </motion.p>

      {/* Selector de tipo de usuario */}
      <div className="relative group mt-8">
        <Label
          htmlFor="userType"
          className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300"
        >
          Tipo de usuario
        </Label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <User className={`h-5 w-5 ${userType ? "text-blue-600 dark:text-blue-400" : "text-gray-400"}`} />
          </div>
          <Select 
            onValueChange={(value) => handleUserTypeChange(value)}
            onOpenChange={(open) => {
              setIsSelectOpen(open);
              if (open) {
                document.body.style.overflow = 'hidden';
              } else {
                document.body.style.overflow = '';
              }
            }}
          >
            <SelectTrigger className="w-full h-10 xl:h-11 rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm  transition-all duration-200 group-hover:border-gray-400 dark:group-hover:border-gray-500 bg-white/90 backdrop-blur-sm ">
              <SelectValue placeholder="Selecciona tipo de usuario" />
            </SelectTrigger>
            <SelectContent
              position="popper"
              className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-md z-[9999999] max-h-[200px] overflow-y-auto radix-select-content "
              sideOffset={5}
              align="center"
              onCloseAutoFocus={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => {
                e.preventDefault();
                setIsSelectOpen(false);
                document.body.style.overflow = '';
              }}
              onPointerDownOutside={(e) => {
                e.preventDefault();
                setIsSelectOpen(false);
                document.body.style.overflow = '';
              }}
            >
              {rol.map((role) => (
                <SelectItem
                  key={role.rolId}
                  value={role.rolId.toString()}
                  className="capitalize"
                >
                  <span className="capitalize">
                    {role.authority.replace("ROLE_", "")}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Campo de email */}
      <div className="relative group">
        <Label
          htmlFor="email"
          className={`block text-xs font-medium mb-1 transition-colors duration-200 ${!isEmailValid ? "text-red-500" : isFocusedEmail ? "text-blue-600 dark:text-blue-400" : "text-gray-700 dark:text-gray-300"
            }`}
        >
          Correo electrónico
        </Label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className={`h-5 w-5 ${!isEmailValid ? "text-red-500" : isFocusedEmail ? "text-blue-600 dark:text-blue-400" : "text-gray-400"
              }`} />
          </div>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onFocus={() => setIsFocusedEmail(true)}
            onBlur={() => setIsFocusedEmail(false)}
            className={`w-full h-10 xl:h-11 rounded-lg border ${!isEmailValid
                ? "border-red-500 focus-visible:ring-red-500"
                : isFocusedEmail
                  ? "border-blue-500 focus-visible:ring-blue-500"
                  : "border-gray-300 dark:border-gray-600"
              } shadow-sm transition-all duration-200 group-hover:border-gray-400 dark:group-hover:border-gray-500 bg-white/90 backdrop-blur-sm placeholder:text-[.85rem]`}
            placeholder="correo@ejemplo.com"
          />
        </div>
        {!isEmailValid && (
          <p className="text-red-500 text-xs mt-1 flex items-center">
            <span className="inline-block mr-1">•</span>
            Correo electrónico no válido
          </p>
        )}
      </div>

      {/* Campo de contraseña */}
      <div className="relative group">
        <Label
          htmlFor="password"
          className={`block text-xs font-medium mb-1 transition-colors duration-200 ${!isPasswordValid ? "text-red-500" : isFocusedPassword ? "text-blue-600 dark:text-blue-400" : "text-gray-700 dark:text-gray-300"
            }`}
        >
          Contraseña
        </Label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className={`h-5 w-5 ${!isPasswordValid ? "text-red-500" : isFocusedPassword ? "text-blue-600 dark:text-blue-400" : "text-gray-400"
              }`} />
          </div>
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={handlePasswordChange}
            onFocus={() => setIsFocusedPassword(true)}
            onBlur={() => setIsFocusedPassword(false)}
            className={`w-full h-10 xl:h-11 rounded-lg border ${!isPasswordValid
                ? "border-red-500 focus-visible:ring-red-500"
                : isFocusedPassword
                  ? "border-blue-500 focus-visible:ring-blue-500"
                  : "border-gray-300 dark:border-gray-600"
              } shadow-sm transition-all duration-200 group-hover:border-gray-400 dark:group-hover:border-gray-500 bg-white/90 backdrop-blur-sm`}
            placeholder="••••••••"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 z-10"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
          >
            {showPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
        </div>
        {!isPasswordValid && (
          <p className="text-red-500 text-xs mt-1 flex items-center">
            <span className="inline-block mr-1">•</span>
            La contraseña debe tener al menos 8 caracteres, una letra mayúscula, una minúscula y un número
          </p>
        )}
      </div>

      {/* Botón de siguiente */}
      <motion.button
        type="button"
        disabled={!email.trim() || !isEmailValid || !password.trim() || !isPasswordValid || !userType}
        onClick={handleNextStep}
        whileHover={{ scale: email.trim() && isEmailValid && password.trim() && isPasswordValid && userType ? 1.02 : 1 }}
        whileTap={{ scale: email.trim() && isEmailValid && password.trim() && isPasswordValid && userType ? 0.98 : 1 }}
        className={`w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-md text-sm font-medium text-white transition-all duration-300 relative z-10 h-10 xl:h-11 mt-6 ${email.trim() && isEmailValid && password.trim() && isPasswordValid && userType
            ? "bg-gradient-to-r from-blue-600 via-indigo-500 to-blue-600 hover:from-blue-700 hover:via-indigo-600 hover:to-blue-700 cursor-pointer active:scale-[0.98] hover:shadow-lg"
            : "bg-gray-400 cursor-not-allowed opacity-70"
          }`}
      >
        <span>Siguiente</span>
        <motion.div
          animate={{ x: [0, 4, 0] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <ChevronRight className="h-5 w-5" />
        </motion.div>
      </motion.button>

      {/* Enlace para iniciar sesión */}
      <div className="text-center mt-6">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          ¿Ya tienes una cuenta?{" "}
          <Link href="/login" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 relative z-10 hover:underline">
            Inicia sesión
          </Link>
        </p>
      </div>
    </div>
  );
} 